import { map, Observable, of, switchMap, timer } from 'rxjs';
import { BaseApi } from '../base-api';
import { Injectable } from '@angular/core';
import { PaginationMeta } from '@app/shared/components/data-table/data-table.component';
import { HttpClient, HttpParams } from '@angular/common/http';

export interface ApiResponse {
    status: string;
    message: string;
}

export interface ProfileUpdateStatus {
    required: boolean;
    lastUpdated: any;
    nextReminderDate: any;
}

export interface InvestUserResponse {
    id: number;
    governing_body?: string;
    governing_body_other?: string;
    registration_name?: string;
    registered_date?: string;
    established_date?: string;
    position?: string;
    code?: string;
    entity_type_id?: number;
    created_at?: string;
    updated_at?: string;

    landline: string;
    mobile: string;
    address: string;
    date_of_incorporation: string;
    logo_url: string;
    narrative_profile: string;
    authorized_representative: {
        first_name: string;
        middle_name: string;
        last_name: string;
        position: string;
    };

    corporate_name: string;
    email: string;
    type: string;
    investee_code: string;
    invest_id: number;
    registration_date: string;
    last_updated: string;
    status: string;
    blocked_user_id: string;
    blocked_at: string;
    has_report: string;
    pinned_entities: [
        {
            id: number;
            registration_name: string;
            code: string;
            pinned_at: string;
            invest_entity_id: string;
            pinned_entity_id: string;
        }
    ];
    connections: [
        {
            investor_id: number;
            investee_id: number;
            status: string;
            nickname: string;
        }
    ];

    investor: {
        id: number;
        corporate_name: string;
        code: string;
    };
    investee: {
        id: number;
        corporate_name: string;
        code: string;
    };
    sender: number;
    receiver: number;
    status_id: any;
    investor_muted: boolean;
    investee_muted: boolean;
    nickname: string;
}

export interface InvestNarrativeResponse {
    id: number;
    invest_entity_id?: string;
    content?: string;
    draft?: string;
    updated_at?: string;
    isOwner?: boolean;
}

export interface InvestUserRequest {
    code: string;
    entity_type_id: string;
    governing_body: string;
    registration_name: string;
    department: string;
    invest_entity_id: string;
    pinned_entity_id: string;
    investor_id: string;
    investee_id: string;
    status_id: string;
    content: any;
    receiver_id: string;
    nickname: string;
    muted_by: string;
    investor_muted: boolean;
    investee_muted: boolean;
    type: string;
    is_draft: boolean;
    image_ids?: number[];
    latest: boolean;
}

export interface UpdateProfileResponse {
    invest_entity_id: number;
    registration_name: string;
    needs_update: boolean;
    last_updated_at: string | null;
    next_reminder_date: string | null;
}

export interface InvestListResponse extends PaginationMeta {
    data: InvestUserResponse[];
}

export interface InvestNarrativeResponse {
    data: InvestNarrativeResponse;
}

export interface UserData {
    data: InvestUserResponse;
}

@Injectable({
    providedIn: 'root',
})
export class InvestService extends BaseApi {
    private stateKey = 'invest-profile';
    private state: any = null;

    setState(data: any) {
        this.state = data;
        sessionStorage.setItem(this.stateKey, JSON.stringify(data));
    }

    getState(): any {
        if (!this.state) {
            const stored = sessionStorage.getItem(this.stateKey);
            if (stored) {
                this.state = JSON.parse(stored);
            }
        }
        return this.state;
    }

    clearState() {
        this.state = null;
        sessionStorage.removeItem(this.stateKey);
    }

    getAlertReminder(): Observable<ApiResponse> {
        return this.get<ApiResponse>('investor-investee/profile', {});
    }

    getAllUsers(options: HttpParams): Observable<InvestListResponse> {
        return this.get<InvestListResponse>('investor-investee?context=external&status=active&registration_status=9', {
            params: options,
        });
    }

    getAllRequests(options: HttpParams): Observable<InvestListResponse> {
        return this.get<InvestListResponse>('connection-requests', {
            params: options,
        });
    }

    getUserById(id: number): Observable<UserData> {
        return this.get<UserData>(`-users/${id}?include=roles&=true`);
    }

    connectUser(data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>('connection-requests/connect', data);
    }

    acceptRequest(data: Partial<InvestUserRequest>): Observable<InvestUserResponse> {
        return this.post<InvestUserResponse>('connection-requests/accept', data);
    }

    disconnect(data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>('connection-requests/disconnect', data);
    }

    pin(data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>(`investor-investee/pin`, data);
    }

    unpin(data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>(`investor-investee/unpin`, data);
    }

    mute(data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>(`connection-requests/mute`, data);
    }

    unmute(data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>(`connection-requests/unmute`, data);
    }

    addNickname(data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>(`connection-requests/assign`, data);
    }

    report(data: FormData | Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>('investor-investee/report', data);
    }

    feedback(data: FormData | Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.post<ApiResponse>('connection-requests/feedback', data);
    }

    getInvestProfile(id: number): Observable<InvestNarrativeResponse> {
        return this.get<InvestNarrativeResponse>(`investor-investee/profile/${id}`);
    }

    updateProfileNarrative(id: number, data: Partial<InvestUserRequest>): Observable<ApiResponse> {
        return this.patch<ApiResponse>(`investor-investee/profile/${id}`, data);
    }

    updateProfileNarrativeWithImages(id: number, formData: FormData): Observable<ApiResponse> {
        // Laravel requires _method field for PATCH with FormData
        formData.append('_method', 'PATCH');
        return this.post<ApiResponse>(`investor-investee/profile/${id}`, formData);
    }

    checkProfileUpdateRequired(userId: string): Observable<ProfileUpdateStatus> {
        if (!userId) {
            return new Observable<ProfileUpdateStatus>((observer) => {
                observer.next({ required: false, lastUpdated: null, nextReminderDate: null });
                observer.complete();
            });
        }

        return this.get<UpdateProfileResponse>(`investor-investee/profile/${userId}/last-updated`).pipe(
            map((res) => {
                const lastUpdated = res.last_updated_at ? new Date(res.last_updated_at) : null;

                return {
                    required: res.needs_update ?? false,
                    lastUpdated: lastUpdated?.toISOString ?? null,
                    nextReminderDate: res.next_reminder_date ?? null,
                    registrationName: res.registration_name
                };
            })
        );
    }

    pollProfileUpdateRequired(userId: string, checkEveryMs: number = 60000): Observable<ProfileUpdateStatus> {
        return timer(0, checkEveryMs).pipe(switchMap(() => this.checkProfileUpdateRequired(userId)));
    }

    getPublicConfigurations(): Observable<any[]> {
        return this.get<any[]>('system-configurations/public');
    }
}
